import { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Button,
  Input,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Badge,
  useToast,
} from "@asm/ui";
import { Search, Trash2, Download, ChevronLeft, ChevronRight, AlertTriangle } from "lucide-react";
import { adminApi, ReconDomain, PaginatedReconDomains } from "@/lib/api";
import { ReconImport } from "./ReconImport";

interface ReconDomainsProps {
  onImportComplete: () => void;
}

export function ReconDomains({ onImportComplete }: ReconDomainsProps) {
  const { toast } = useToast();
  const [data, setData] = useState<PaginatedReconDomains | null>(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [tldFilter, setTldFilter] = useState("");
  const [tlds, setTlds] = useState<string[]>([]);
  const [showImport, setShowImport] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [downloading, setDownloading] = useState<string | null>(null);
  const [showPurge, setShowPurge] = useState(false);
  const [purgeConfirmation, setPurgeConfirmation] = useState("");
  const [purging, setPurging] = useState(false);

  const perPage = 20;

  const fetchDomains = async () => {
    try {
      setLoading(true);
      const result = await adminApi.listReconDomains({
        page,
        per_page: perPage,
        tld: tldFilter || undefined,
        search: search || undefined,
      });
      setData(result);
    } catch (err) {
      toast({
        title: "Error",
        description: err instanceof Error ? err.message : "Failed to load domains",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchTlds = async () => {
    try {
      const result = await adminApi.getReconTlds();
      setTlds(result.tlds);
    } catch (err) {
      console.error("Failed to load TLDs:", err);
    }
  };

  useEffect(() => {
    fetchDomains();
  }, [page, tldFilter]);

  useEffect(() => {
    fetchTlds();
  }, []);

  const handleSearch = () => {
    setPage(1);
    fetchDomains();
  };

  const handleDelete = async (domain: ReconDomain) => {
    if (!confirm(`Delete domain ${domain.domain}?`)) return;

    setDeleting(domain.id);
    try {
      await adminApi.deleteReconDomain(domain.id);
      toast({
        title: "Domain Deleted",
        description: `${domain.domain} has been removed.`,
      });
      fetchDomains();
    } catch (err) {
      toast({
        title: "Error",
        description: err instanceof Error ? err.message : "Failed to delete domain",
        variant: "destructive",
      });
    } finally {
      setDeleting(null);
    }
  };

  const handleDownload = async (domain: ReconDomain) => {
    setDownloading(domain.id);
    try {
      await adminApi.downloadReconResult(domain.id, domain.domain);
      toast({
        title: "Download Started",
        description: `Downloading results for ${domain.domain}`,
      });
    } catch (err) {
      toast({
        title: "Download Failed",
        description: err instanceof Error ? err.message : "Failed to download results",
        variant: "destructive",
      });
    } finally {
      setDownloading(null);
    }
  };

  const handlePurge = async () => {
    setPurging(true);
    try {
      const result = await adminApi.purgeReconDomains();
      toast({
        title: "Database Purged",
        description: `Deleted ${result.domains_deleted.toLocaleString()} domains and ${result.results_deleted.toLocaleString()} results.`,
      });
      setShowPurge(false);
      setPurgeConfirmation("");
      fetchDomains();
      fetchTlds();
      onImportComplete(); // refresh stats
    } catch (err) {
      toast({
        title: "Error",
        description: err instanceof Error ? err.message : "Failed to purge database",
        variant: "destructive",
      });
    } finally {
      setPurging(false);
    }
  };

  const handleImportComplete = () => {
    setShowImport(false);
    fetchDomains();
    fetchTlds();
    onImportComplete();
  };

  const totalPages = data ? Math.ceil(data.total / perPage) : 0;

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return "Never";
    return new Date(dateStr).toLocaleDateString();
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Domain Database</CardTitle>
              <CardDescription>
                {data?.total.toLocaleString() || 0} domains in database
              </CardDescription>
            </div>
            <div className="flex gap-2">
              <Button variant="destructive" onClick={() => setShowPurge(true)} disabled={!data?.total}>
                <Trash2 className="h-4 w-4 mr-2" />
                Delete All
              </Button>
              <Button onClick={() => setShowImport(true)}>
                <Download className="h-4 w-4 mr-2" />
                Import Domains
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Filters */}
          <div className="flex gap-4 mb-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search domains..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                  className="pl-8"
                />
              </div>
            </div>
            <select
              value={tldFilter}
              onChange={(e) => {
                setTldFilter(e.target.value);
                setPage(1);
              }}
              className="flex h-10 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              <option value="">All TLDs</option>
              {tlds.map((tld) => (
                <option key={tld} value={tld}>
                  .{tld}
                </option>
              ))}
            </select>
            <Button variant="outline" onClick={handleSearch}>
              Search
            </Button>
          </div>

          {/* Table */}
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
            </div>
          ) : (
            <>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Domain</TableHead>
                      <TableHead>Source</TableHead>
                      <TableHead>TLD</TableHead>
                      <TableHead>Scans</TableHead>
                      <TableHead>Last Scanned</TableHead>
                      <TableHead>Modules</TableHead>
                      <TableHead className="w-[50px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data?.domains.map((domain) => (
                      <TableRow key={domain.id}>
                        <TableCell className="font-mono text-sm">
                          {domain.domain}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{domain.source}</Badge>
                        </TableCell>
                        <TableCell>.{domain.tld}</TableCell>
                        <TableCell>{domain.scan_count}</TableCell>
                        <TableCell className="text-muted-foreground">
                          {formatDate(domain.last_scanned_at)}
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {domain.has_dns && <Badge variant="secondary" className="text-xs">DNS</Badge>}
                            {domain.has_email_security && <Badge variant="secondary" className="text-xs">Email</Badge>}
                            {domain.has_tls && <Badge variant="secondary" className="text-xs">TLS</Badge>}
                            {domain.has_whois && <Badge variant="secondary" className="text-xs">WHOIS</Badge>}
                            {domain.has_headers && <Badge variant="secondary" className="text-xs">Hdrs</Badge>}
                            {domain.has_cloud && <Badge variant="secondary" className="text-xs">Cloud</Badge>}
                            {domain.has_asn && <Badge variant="secondary" className="text-xs">ASN</Badge>}
                            {domain.has_tech && <Badge variant="secondary" className="text-xs">Tech</Badge>}
                            {domain.has_repos && <Badge variant="secondary" className="text-xs">Repos</Badge>}
                            {domain.has_crawl_hints && <Badge variant="secondary" className="text-xs">Crawl</Badge>}
                            {domain.has_js_analysis && <Badge variant="secondary" className="text-xs">JS</Badge>}
                            {domain.has_favicon && <Badge variant="secondary" className="text-xs">Fav</Badge>}
                            {domain.has_typosquat && <Badge variant="secondary" className="text-xs">Typo</Badge>}
                            {domain.has_ipintel && <Badge variant="secondary" className="text-xs">IP</Badge>}
                            {domain.has_vuln && <Badge variant="secondary" className="text-xs">Vuln</Badge>}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDownload(domain)}
                              disabled={domain.scan_count === 0 || downloading === domain.id}
                              title={domain.scan_count === 0 ? "No scan results to download" : "Download latest results (.json.gz)"}
                            >
                              <Download className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDelete(domain)}
                              disabled={deleting === domain.id}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                    {data?.domains.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                          No domains found. Import some to get started.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4">
                  <div className="text-sm text-muted-foreground">
                    Showing {((page - 1) * perPage) + 1} to {Math.min(page * perPage, data?.total || 0)} of {data?.total || 0}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page === 1}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="text-sm">
                      Page {page} of {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Import Dialog */}
      {showImport && (
        <ReconImport
          onClose={() => setShowImport(false)}
          onComplete={handleImportComplete}
        />
      )}

      {/* Purge Confirmation Dialog */}
      {showPurge && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <Card className="w-full max-w-md mx-4">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-destructive">
                <AlertTriangle className="h-5 w-5" />
                Delete All Domains
              </CardTitle>
              <CardDescription>
                This will permanently delete <strong>{data?.total.toLocaleString() || 0} domains</strong> and
                all associated scan results. This action cannot be undone.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">
                  Type <span className="font-mono text-destructive">DELETE ALL</span> to confirm
                </label>
                <Input
                  value={purgeConfirmation}
                  onChange={(e) => setPurgeConfirmation(e.target.value)}
                  placeholder="DELETE ALL"
                  autoFocus
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowPurge(false);
                    setPurgeConfirmation("");
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={handlePurge}
                  disabled={purgeConfirmation !== "DELETE ALL" || purging}
                >
                  {purging ? "Deleting..." : "Delete Everything"}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
